import ApiService from '@/common/api.service'

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param location Object
 *
 * @return Object
 */
function transform (location) {
  return {
    id: location.id,
    location_type: location.location_type,
    address: location.address,
    place_id: location.place_id,
    latitude: location.latitude,
    longitude: location.longitude
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
export function defaults () {
  return {
    isEditing: false,
    isAdding: false,
    isDeleting: false,
    warning: '',
    locations: [],
    location_types: {}
  }
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns an array of locations
   *
   * @return Array
   */
  locations (state) {
    return state.locations
  },

  /**
   * Returns the list of location types.
   *
   * @param state Object
   *
   * @return Array
   */
  locationTypes (state) {
    return state.location_types
  },

  /**
   * Returns the current form warning.
   *
   * @param state Object
   *
   * @return string
   */
  warning (state) {
    return state.warning
  }
}

export const actions = {
  /**
   * API request to create a new location. The payload must be in the format:
   * { url: String, data: Object }
   *
   * @return Promise
   */
  post (context, payload) {
    return ApiService
      .post(payload.url, transform(payload.data))
      .then((response) => {
        context.commit('add', response.data.data)
        return response
      })
  },

  /**
   * API request to update an existing location. The payload must be in the format:
   * { url: String, data: Object }
   *
   * @return Promise
   */
  put (context, payload) {
    return ApiService
      .put(payload.url, transform(payload.data))
      .then((response) => {
        context.commit('update', response.data.data)
        return response
      })
  },

  /**
   * API request to delete an existing location. The payload must be in the format:
   * { url: String, data: Object  }
   *
   * @return Promise
   */
  delete (context, payload) {
    return ApiService
      .delete(payload.url)
      .then((response) => {
        context.commit('removeById', payload.data.id)
        return response
      })
  }
}

export const mutations = {
  /**
   * Set an array of locations.
   *
   * @param state    Object
   * @param locations Array
   */
  setLocations (state, locations) {
    state.locations = locations
  },

  /**
   * Set the location types.
   *
   * @param state        Object
   * @param locationTypes Object
   */
  setLocationTypes (state, locationTypes) {
    state.location_types = locationTypes
  },

  /**
   * Add a new location.
   *
   * @param state   Object
   * @param location Object
   */
  add (state, location) {
    state.locations.push(location)
  },

  /**
   * Updates an existing location.
   *
   * @param state   Object
   * @param location Object
   */
  update (state, location) {
    let index = state.locations.findIndex(element => element.id === location.id)
    state.locations[index] = location
  },

  /**
   * Find a location by index in the array of locations and updates it.
   *
   * @param state   Object
   * @param location Object
   * @param index   Number
   */
  updateByIndex (state, {location, index}) {
    state.locations[index] = location
  },

  /**
   * Updates the last location that was added.
   *
   * @param state   Object
   * @param location Object
   */
  updateNewest (state, location) {
    state.locations[state.locations.length - 1] = location
  },

  /**
   * Find a location by id and remove it.
   *
   * @param state Object
   * @param id    Number
   */
  removeById (state, id) {
    let index = state.locations.findIndex(element => element.id === id)
    state.locations.splice(index, 1)
  },

  /**
   * Remove a location by index in the array of locations.
   *
   * @param state Object
   * @param index Number
   */
  removeByIndex (state, index) {
    state.locations.splice(index, 1)
  },

  /**
   * Clears both the locations and location types from the state.
   *
   * @param state Object
   */
  purge (state) {
    state.locations = []
    state.location_types = {}
  },

  /**
   * Purge the location types only and not the location types.
   *
   * @param state Object
   */
  purgeLocations (state) {
    state.locations = []
  },

  /**
   * Sets the isAdding form state.
   *
   * @param state   Object
   * @param isAdding Boolean
   */
  setIsAdding (state, isAdding) {
    state.isAdding = isAdding
  },

  /**
   * Sets the isEditing form state.
   *
   * @param state     Object
   * @param isEditing Boolean
   */
  setIsEditing (state, isEditing) {
    state.isEditing = isEditing
  },

  /**
   * Sets the isDeleting form state.
   *
   * @param state      Object
   * @param isDeleting Boolean
   */
  setIsDeleting (state, isDeleting) {
    state.isDeleting = isDeleting
  },

  /**
   * Sets the form warning.
   *
   * @param state      Object
   * @param warning Boolean
   */
  setWarning (state, warning) {
    state.warning = warning
  },

  /**
   * Resets the form's state.
   *
   * @param state      Object
   */
  resetFormState (state) {
    state.isAdding = false
    state.isEditing = false
    state.isDeleting = false
    state.warning = ''
  }
}

export default {
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/locations/locationsModule.js